import { promises as fsp } from 'node:fs';
import * as np from 'node:path';
import type { IFilesystem, FileMetadata } from '@livx.cc/wcli/core';
import { PathResolver } from '@livx.cc/wcli/core';

/**
 * Real-disk backend implementing wcli's IFilesystem, rooted at `baseDir`.
 * The VFS path space ('/...') maps under baseDir, so the same agent/tools/commands
 * run unchanged against disk or memory — complete mem↔disk interchangeability.
 * Semantics mirror MemFilesystem (throws on missing parent / existing dir / etc.).
 */
export class NodeDiskFilesystem implements IFilesystem {
  private cwd = '/';
  private opts: { denySymlinks: boolean };

  constructor(private baseDir: string, opts: { denySymlinks?: boolean } = {}) {
    // denySymlinks (default ON): refuse to traverse a symlink anywhere in the path, so
    // a symlink planted inside the root cannot escape it (read/write/delete a target outside).
    this.opts = { denySymlinks: true, ...opts };
  }

  /** Ensure the root dir exists. */
  async init(): Promise<void> {
    await fsp.mkdir(this.baseDir, { recursive: true });
  }

  private real(vpath: string): string {
    return np.join(this.baseDir, '.' + vpath); // vpath is absolute in VFS space
  }

  // Verified non-symlink DIRECTORY components, with a short TTL: tree walks (Glob/Grep) hit the same
  // parents thousands of times; re-lstat'ing each per op is the dominant syscall cost. The 1s window
  // is an accepted race only against an out-of-band symlink swap mid-walk (this FS can't create
  // symlinks itself); leaf components are never cached.
  private verified = new Map<string, number>();
  private static VERIFY_TTL_MS = 1000;

  /** Throw if any existing component of `real` is a symlink (escape vector). */
  private async assertNoSymlink(real: string): Promise<void> {
    if (!this.opts.denySymlinks) return;
    const rel = np.relative(this.baseDir, real);
    if (rel === '' || rel.startsWith('..')) return; // root itself / outside (can't happen post-join)
    const parts = rel.split(np.sep);
    let cur = this.baseDir;
    const now = Date.now();
    for (let i = 0; i < parts.length; i++) {
      cur = np.join(cur, parts[i]);
      const isLeaf = i === parts.length - 1;
      if (!isLeaf && (this.verified.get(cur) ?? 0) > now) continue;
      let st;
      try { st = await fsp.lstat(cur); } catch { return; } // component doesn't exist yet (e.g. new file) — nothing further to check
      if (st.isSymbolicLink()) throw new Error('File not found: symlink not permitted');
      if (!isLeaf) {
        if (this.verified.size > 10_000) this.verified.clear(); // bound memory on huge trees
        this.verified.set(cur, now + NodeDiskFilesystem.VERIFY_TTL_MS);
      }
    }
  }

  resolvePath(path: string, cwd?: string): string {
    return PathResolver.resolve(path, cwd || this.cwd);
  }
  getCwd(): string { return this.cwd; }
  setCwd(path: string): void { this.cwd = PathResolver.normalize(path); }

  async readFile(path: string): Promise<string> {
    const r = this.real(this.resolvePath(path));
    try {
      await this.assertNoSymlink(r);
      const st = await fsp.stat(r);
      if (st.isDirectory()) throw new Error(`Not a file: ${path}`);
      return await fsp.readFile(r, 'utf8');
    } catch (e) {
      if (e instanceof Error && /Not a file/.test(e.message)) throw e;
      throw new Error(`File not found: ${path}`);
    }
  }

  /** The REAL on-disk path behind a VFS path — an optional capability (not on IFilesystem) the Read
   *  tool duck-types so it can persist an image REFERENCE instead of megabytes of base64. */
  realPath(path: string): string { return this.real(this.resolvePath(path)); }

  /** Read raw bytes (for binary files like images). Not on the base IFilesystem (which is utf8-only) — an
   *  optional capability the Read tool duck-types to return image blocks. Same symlink/jail guards as readFile. */
  async readFileBytes(path: string): Promise<Uint8Array> {
    const r = this.real(this.resolvePath(path));
    try {
      await this.assertNoSymlink(r);
      const st = await fsp.stat(r);
      if (st.isDirectory()) throw new Error(`Not a file: ${path}`);
      return await fsp.readFile(r); // no encoding → Buffer (Uint8Array)
    } catch (e) {
      if (e instanceof Error && /Not a file/.test(e.message)) throw e;
      throw new Error(`File not found: ${path}`);
    }
  }

  async writeFile(path: string, content: string): Promise<void> {
    const r = this.real(this.resolvePath(path));
    await this.assertNoSymlink(r);
    const parent = np.dirname(r);
    try {
      if (!(await fsp.stat(parent)).isDirectory()) throw 0;
    } catch {
      throw new Error(`Parent directory does not exist: ${path}`);
    }
    await fsp.writeFile(r, content, 'utf8');
  }

  async deleteFile(path: string): Promise<void> {
    const r = this.real(this.resolvePath(path));
    await this.assertNoSymlink(r);
    let st;
    try { st = await fsp.stat(r); } catch { throw new Error(`File not found: ${path}`); }
    if (st.isDirectory()) {
      if ((await fsp.readdir(r)).length > 0) throw new Error(`Directory not empty: ${path}`);
      await fsp.rmdir(r);
    } else {
      await fsp.unlink(r);
    }
  }

  async readDir(path: string): Promise<string[]> {
    const r = this.real(this.resolvePath(path));
    try { await this.assertNoSymlink(r); return await fsp.readdir(r); }
    catch { throw new Error(`Directory not found: ${path}`); }
  }

  async createDir(path: string): Promise<void> {
    if (await this.exists(path)) throw new Error(`File or directory already exists: ${path}`);
    const r = this.real(this.resolvePath(path));
    await this.assertNoSymlink(r);
    const parent = np.dirname(r);
    try {
      if (!(await fsp.stat(parent)).isDirectory()) throw 0;
    } catch {
      throw new Error(`Parent directory does not exist: ${path}`);
    }
    await fsp.mkdir(r);
  }

  async exists(path: string): Promise<boolean> {
    const r = this.real(this.resolvePath(path));
    try { await this.assertNoSymlink(r); await fsp.stat(r); return true; } catch { return false; }
  }

  async stat(path: string): Promise<FileMetadata> {
    let s;
    const r = this.real(this.resolvePath(path));
    try { await this.assertNoSymlink(r); s = await fsp.stat(r); }
    catch { throw new Error(`File not found: ${path}`); }
    return {
      created: s.birthtime,
      modified: s.mtime,
      size: s.size,
      permissions: s.isDirectory() ? 'drwxr-xr-x' : '-rw-r--r--',
      isExecutable: false,
    };
  }

  async isDirectory(path: string): Promise<boolean> {
    const r = this.real(this.resolvePath(path));
    try { await this.assertNoSymlink(r); return (await fsp.stat(r)).isDirectory(); } catch { return false; }
  }
  async isFile(path: string): Promise<boolean> {
    const r = this.real(this.resolvePath(path));
    try { await this.assertNoSymlink(r); return (await fsp.stat(r)).isFile(); } catch { return false; }
  }
}
